import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../mockData';
import { ShieldCheck, GraduationCap, SearchCheck, Users, Eye, AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'shield-check': ShieldCheck,
  'graduation-cap': GraduationCap,
  'search-check': SearchCheck,
  'users': Users,
  'eye': Eye,
  'alert-triangle': AlertTriangle
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const serviceRefs = useRef({});

  // Handle scroll to specific service when navigating with hash
  useEffect(() => {
    if (location.hash) {
      const serviceId = location.hash.replace('#', '');
      // Wait for DOM to render
      setTimeout(() => {
        const element = serviceRefs.current[serviceId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: `Jag är intresserad av tjänsten: ${service.title}`
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const orderData = {
        service_title: selectedService.title,
        ...formData
      };

      const response = await axios.post(`${BACKEND_URL}/api/service-order`, orderData);

      if (response.data.success) {
        toast({
          title: "Beställning mottagen!",
          description: response.data.message,
        });

        setSelectedService(null);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting service order:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte skicka beställningen. Försök igen senare.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Våra <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">Tjänster</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Professionella detektivtjänster anpassade för dina behov
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || ShieldCheck;
              const serviceSlug = service.title.toLowerCase().replace(/\s+/g, '-').replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/&/g, '');
              return (
                <div
                  key={service.id}
                  id={serviceSlug}
                  ref={(el) => serviceRefs.current[serviceSlug] = el}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 hover:border-teal-500/50 transition-all duration-300 group cursor-pointer"
                  onClick={() => handleServiceClick(service)}
                >
                  <div className="mb-6">
                    <div className="w-14 h-14 bg-teal-500/10 rounded-lg flex items-center justify-center group-hover:bg-teal-500/20 transition-all">
                      <IconComponent className="w-7 h-7 text-teal-400" />
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="mb-6 space-y-2">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Check className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        <span className="text-sm text-gray-400">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-6 border-t border-slate-700">
                    <Button
                      className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleServiceClick(service);
                      }}
                    >
                      Offertförfrågan <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Har du ett ärende som inte passar in i våra tjänster?
          </h2>
          <p className="text-gray-400 mb-8">
            Vi anpassar varje uppdrag efter din situation och tar fram en lösning som passar just dig.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300"
          >
            Kontakta oss för en kostnadsfri och konfidentiell konsultation
          </Link>
        </div>
      </section>

      {/* Service Order Dialog - Fixed for mobile scroll */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl">
              {selectedService?.title}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Fyll i formuläret nedan så återkommer vi med en offert inom 24 timmar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 pb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Namn *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="Ditt namn"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  E-post *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="din@email.se"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Telefon (valfritt)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="070-123 45 67"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Meddelande *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  placeholder="Beskriv dina behov..."
                />
              </div>

              <div className="flex space-x-4 pt-2 sticky bottom-0 bg-slate-900">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedService(null)}
                  disabled={isSubmitting}
                  className="flex-1 border-slate-700 hover:bg-slate-800"
                >
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 hover:shadow-lg hover:shadow-teal-500/50"
                >
                  {isSubmitting ? 'Skickar...' : 'Skicka förfrågan'}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Services;
